import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ActionButton } from '@/shared/ui/action-button';

import { type Contact } from '@/entities/contacts';

import { useContactForm, ModalMode, getModalTitle } from '../model';
import { ContactFormFields } from './contact-form-fields';
import { ContactFormSkeleton } from './contact-form-skeleton';
import { SubmitButton } from './submit-button';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (contact: Contact) => void;
  /** The contact to view or edit; loaded in full when the modal opens. */
  contactId?: string;
  /** The organization a created contact belongs to. */
  orgId?: number;
  mode?: ModalMode;
}

export const ContactModal = ({ isOpen, onClose, onSuccess, contactId, orgId, mode = 'create' }: ContactModalProps) => {
  const { t } = useTranslation();
  const isViewMode = mode === 'view';
  const { form, onSubmit, isLoading, loadFailed, isSaving, onCancel } = useContactForm({
    isOpen,
    mode,
    contactId,
    orgId,
    onSuccess,
    onCancel: onClose
  });

  const {
    register,
    formState: { errors }
  } = form;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b bg-muted/50">
          <DialogTitle className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
            {getModalTitle(mode, t)}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="p-6 max-h-[80vh] overflow-y-auto">
          {loadFailed ? (
            <Alert variant="destructive">
              <AlertDescription>
                {t('contacts.error.loadFailed', 'Failed to load the contact details.')}
              </AlertDescription>
            </Alert>
          ) : isLoading ? (
            <ContactFormSkeleton />
          ) : (
            <ContactFormFields register={register} errors={errors} isViewMode={isViewMode} />
          )}
        </form>

        <DialogFooter className="p-4 border-t bg-muted flex flex-row justify-end gap-3">
          <ActionButton type="button" intent="dismiss" onClick={onCancel} className="h-10 px-6">
            {isViewMode || loadFailed ? t('gen.closedialog.label') : t('gen.cancel.label')}
          </ActionButton>
          {!isViewMode && !loadFailed && (
            <SubmitButton onClick={onSubmit} disabled={isSaving || isLoading} isSaving={isSaving} />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
